/* tslint:disable:no-multiple-render-methods */
/**
 * Typeahead is a special case, due to the fact that it is a UITK component,
 * and there are many clients consuming it.
 */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { ContextStore, QueryRoute, updateSearch } from 'bernie-core';
import { ErrorBoundary, Layer } from 'bernie-view';
import { LocalizedText } from 'bernie-l10n';

import { ESSAdapter, TypeaheadRenderType, TypeaheadToolbarVersion, UitkTypeahead } from 'uitk-react-typeahead';
import { UitkHeading } from 'uitk-react-text';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';

import { FormFieldBinding } from './form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form';

export interface HotelNameProps extends SearchFormFieldProps, RouteComponentProps {
  context?: ContextStore;
  regionId?: string;
  analytics?: any;
  maxTypeaheadResults?: number;
}

@withRouter
@FormFieldBinding
@inject('context', 'analytics')
@observer
export class HotelName extends React.Component<HotelNameProps> {
  private inputRef;
  public constructor(props) {
    super(props);
    this.inputRef = React.createRef();

    this.dismissTypeahead = this.dismissTypeahead.bind(this);
    this.openTypeahead = this.openTypeahead.bind(this);
    this.updateTypeahead = this.updateTypeahead.bind(this);
    this.getHotelNameFilterDialog = this.getHotelNameFilterDialog.bind(this);
    this.sendTracking = this.sendTracking.bind(this);
    this.handleClear = this.handleClear.bind(this);
  }

  private handleClear() {
    const { updateModel } = this.props;

    if (typeof updateModel === 'function') {
      updateModel({
        target: {
          name: 'hotelName',
          value: '',
        },
      });
    }
  }

  static defaultProps: Partial<HotelNameProps> = {
    maxTypeaheadResults: 10,
  };

  public render() {
    const { context, model, modelKey, sortAndFilterOptions, showSeparator, maxTypeaheadResults } = this.props;
    const value = (model && model[modelKey]) || '';
    const regionId = model && model.regionId;
    const hotelName = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const label = hotelName && hotelName.label;
    const placeholder = hotelName && hotelName.placeholder;

    if (!hotelName) {
      return null;
    }

    const defaultEssAdapterConfig = {
      client: 'Hotels.Search',
      dest: true,
      features: 'ta_hierarchy|postal_code|contextual_ta|consistent_display',
      guid: context && context.deviceId,
      inputId: 'hotel-name-filter',
      lob: 'HOTELS',
      locale: context && context.locale,
      maxResults: maxTypeaheadResults,
      regionId: parseInt(regionId, 10),
      regionType: 128,
      siteId: context && context.site && context.site.id,
    };

    const defaultTypeaheadProps = {
      allowExactMatch: true,
      icon: 'search',
      name: 'hotelName',
      label: placeholder,
      handleClear: this.handleClear,
      hasClearButton: true,
      onClick: this.openTypeahead,
      onDismiss: this.dismissTypeahead,
      onUpdate: this.updateTypeahead,
      renderDialog: this.getHotelNameFilterDialog,
      renderType: TypeaheadRenderType.DIALOG,
      value,
      toolbarVersion: TypeaheadToolbarVersion.V1,
      typeaheadPlaceholder: placeholder,
      ref: this.inputRef,
      accessibilityLabel: label,
    };

    return (
      <React.Fragment>
        <UitkSpacing border={['blockend', showSeparator ? 'blockstart' : null]} padding={{ block: 'six' }} margin={{ block: 'six' }}>
          <fieldset>
            <UitkLayoutGrid columns={1}>
              <UitkLayoutGridItem>
                <UitkHeading size={5} tag="h3">
                  <UitkSpacing margin={{ blockend: 'two' }}>
                    <legend className="filter-title">{label}</legend>
                  </UitkSpacing>
                </UitkHeading>
              </UitkLayoutGridItem>
              <UitkLayoutGridItem>
                <ErrorBoundary label="hotel-name.typeahead">
                  <UitkTypeahead
                    {...defaultTypeaheadProps}
                    adapter={new ESSAdapter({ ...defaultEssAdapterConfig })}
                    toolbarVersion={TypeaheadToolbarVersion.V2}
                    renderType={TypeaheadRenderType.ADAPTIVE}
                  />
                </ErrorBoundary>
              </UitkLayoutGridItem>
            </UitkLayoutGrid>
          </fieldset>
        </UitkSpacing>
        <UitkHeading tag="h3" size={5}>
          <LocalizedText message="hotels.searchResults.filtersDialog.hotelName.label" />
        </UitkHeading>
      </React.Fragment>
    );
  }

  private dismissTypeahead() {
    this.props.history.goBack();
    if (this.inputRef && this.inputRef.current && typeof this.inputRef.current.focusTypeaheadInput === 'function') {
      this.inputRef.current.focusTypeaheadInput();
    }
  }

  private openTypeahead() {
    const { history, location } = this.props;

    updateSearch({ history, location, newParams: { pwaDialogNested: 'hotel-name-typeahead' } });
  }

  private updateTypeahead(selection) {
    const term = selection.term;

    this.props.updateModel({
      target: {
        name: 'hotelName',
        value: term,
      },
    });
    this.sendTracking(term);
  }

  private sendTracking(term) {
    this.props.analytics.clickTracker(`.hotelName.${term}`, `hotelName.${term}`)();
  }

  private getHotelNameFilterDialog(children) {
    return (
      <QueryRoute query={{ pwaDialogNested: 'hotel-name-typeahead' }}>
        <Layer id="hotel-name-typeahead" onDismiss={this.dismissTypeahead}>
          {children}
        </Layer>
      </QueryRoute>
    );
  }
}
